import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { AuthFrame } from '@/components/AuthFrame';
import { Field } from '@/components/ui/Field';
import { PasswordInput } from '@/components/ui/PasswordInput';
import { authClient } from '@/lib/auth-client';
import { errorText } from '@/lib/errors';
import { client } from '@/lib/orpc';
import { meQuery } from '@/lib/session';

export const Route = createFileRoute('/invite')({ component: AcceptInvite });

/**
 * The invite secret travels in the URL fragment (#hmi_…): fragments never reach
 * a server, so it can't end up in CloudFront/ALB logs. It is read once and
 * removed from the address bar.
 */
function readToken(): string {
  const token = window.location.hash.slice(1);
  if (token) history.replaceState(null, '', window.location.pathname);
  return token;
}

function AcceptInvite() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [token] = useState(readToken);
  const [name, setName] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [formError, setFormError] = useState<string | null>(null);

  const invite = useQuery({
    queryKey: ['invite', token],
    queryFn: () => client.invites.inspect({ token }),
    enabled: token.startsWith('hmi_'),
    retry: false
  });

  const accept = useMutation({
    mutationFn: async () => {
      const { email } = await client.invites.accept({ token, name, password });
      const { error } = await authClient.signIn.email({ email, password });
      return { signedIn: !error };
    },
    onSuccess: async ({ signedIn }) => {
      // The account exists either way; if the automatic sign-in didn't work
      // (e.g. rate limit), the sign-in page takes over instead of an error here.
      if (!signedIn) return void (await navigate({ to: '/login', replace: true }));
      await queryClient.invalidateQueries({ queryKey: meQuery().queryKey });
      await navigate({ to: '/', replace: true });
    },
    onError: (e) => setFormError(errorText(t, e))
  });

  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (password.length < 12) return setFormError(t('auth.passwordTooShort'));
    if (password !== confirm) return setFormError(t('auth.passwordsDiffer'));
    setFormError(null);
    accept.mutate();
  };

  if (!token.startsWith('hmi_') || invite.isError) {
    return (
      <AuthFrame title={t('auth.inviteTitle')}>
        <div className="alert alert-error alert-soft text-sm">{t('auth.inviteInvalid')}</div>
      </AuthFrame>
    );
  }
  if (!invite.data) {
    return (
      <AuthFrame title={t('auth.inviteTitle')}>
        <div className="skeleton h-40 w-full" />
      </AuthFrame>
    );
  }

  return (
    <AuthFrame title={t('auth.inviteTitle')} lead={t('auth.inviteLead', { email: invite.data.email })}>
      <form onSubmit={submit}>
        <Field label={t('auth.yourName')}>
          {(id) => <input id={id} className="input w-full" autoComplete="name" value={name} onChange={(e) => setName(e.target.value)} required autoFocus maxLength={120} />}
        </Field>
        <input type="email" className="hidden" autoComplete="username" value={invite.data.email} readOnly />
        <PasswordInput label={t('auth.password')} value={password} onChange={setPassword} autoComplete="new-password" hint={t('auth.passwordHint')} />
        <PasswordInput label={t('auth.confirmPassword')} value={confirm} onChange={setConfirm} autoComplete="new-password" error={formError} />
        <button type="submit" className="btn btn-primary mt-4 w-full" disabled={accept.isPending}>
          {accept.isPending && <span className="loading loading-spinner loading-sm" />}
          {t('auth.createAccount')}
        </button>
      </form>
    </AuthFrame>
  );
}
